import type { ColumnType } from '../types/columnType';

/**
 * Check if a single value is compatible with a target column type.
 */
function isValueCompatible(value: string | number | null, targetType: ColumnType): boolean {
  // Null/empty values are always compatible (they're just missing data)
  if (value == null || value === '') return true;

  const str = String(value).trim();
  if (str === '') return true;

  switch (targetType) {
    case 'Text':
      return true; // Everything is valid text

    case 'Number': {
      const cleaned = str.replace(/[, ]/g, '');
      return /^-?\d+(\.\d+)?$/.test(cleaned);
    }

    case 'Date':
      return (
        /^\d{4}-\d{2}-\d{2}$/.test(str) ||
        /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(str) ||
        /^[A-Za-z]{3,}\s+\d{1,2},?\s+\d{2,4}$/.test(str) ||
        /^\d{1,2}\s+[A-Za-z]{3,}\s+\d{2,4}$/.test(str) ||
        /^\d{1,2}\.\d{1,2}\.\d{2,4}$/.test(str)
      );

    case 'DateTime':
      return (
        /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(str) ||
        /^\d{1,2}\/\d{1,2}\/\d{2,4}\s+\d{1,2}:\d{2}/.test(str)
      );

    case 'Percentage':
      return (
        /^-?\d+([.,]\d+)?%$/.test(str) ||
        /^-?\d+([.,]\d+)?\s*percent$/i.test(str)
      );

    case 'Currency':
      return (
        /^[$€£¥₹₽]\s*-?\d/.test(str) ||
        /^-?[\d,. ]+\s*[A-Z]{3}$/.test(str) ||
        /^[A-Z]{3}\s+-?[\d,. ]+$/.test(str)
      );

    case 'Boolean': {
      const normalized = str.toLowerCase();
      return ['true', 'false', 'yes', 'no', '1', '0'].includes(normalized);
    }

    case 'URL':
      return /^(https?:\/\/|www\.)[^\s]+$/i.test(str);


    default:
      return true;
  }
}

export interface CompatibilityResult {
  compatible: number;
  incompatible: number;
  total: number;
}

/**
 * Check how many values in a column are compatible with a target type.
 * Null/empty values are counted as compatible.
 */
export function checkCompatibility(
  values: (string | number | null)[],
  targetType: ColumnType
): CompatibilityResult {
  let compatible = 0;
  let incompatible = 0;

  for (const value of values) {
    if (isValueCompatible(value, targetType)) {
      compatible++;
    } else {
      incompatible++;
    }
  }

  return {
    compatible,
    incompatible,
    total: values.length,
  };
}
